import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, BookOpen, Server, Shield, TestTube, RefreshCw, CheckCircle, XCircle, AlertTriangle, Cpu, HardDrive, Database, Activity, Copy, Code, Settings, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../services/api';

export const DocumentationPanel = () => {
  const [activeTab, setActiveTab] = useState('api');
  const [apiDocs, setApiDocs] = useState(null);
  const [archDocs, setArchDocs] = useState(null);
  const [secDocs, setSecDocs] = useState(null);
  const [testDocs, setTestDocs] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [setupDocs, setSetupDocs] = useState(null);
  const [selectedExample, setSelectedExample] = useState(null);
  const [loading, setLoading] = useState({});

  const load = async (key, url, setter) => {
    setLoading(p => ({...p, [key]: true}));
    try { const r = await apiClient.get(url); setter(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(p => ({...p, [key]: false})); }
  };

  useEffect(() => { load('api', '/docs/docs/api', setApiDocs); }, []);

  const loadTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'api' && !apiDocs) load('api', '/docs/docs/api', setApiDocs);
    if (tab === 'arch' && !archDocs) load('arch', '/docs/docs/architecture', setArchDocs);
    if (tab === 'security' && !secDocs) load('security', '/docs/docs/security', setSecDocs);
    if (tab === 'setup' && !setupDocs) load('setup', '/docs/docs/setup', setSetupDocs);
    if (tab === 'tests' && !testDocs) load('tests', '/docs/docs/tests', setTestDocs);
    if (tab === 'health') load('health', '/docs/health/extended', setHealthData);
  };

  const StatusBadge = ({ status }) => {
    const map = { ok: ['bg-green-50 text-green-700 border-green-200', 'OK'], healthy: ['bg-green-50 text-green-700 border-green-200', 'Gesund'], error: ['bg-red-50 text-red-700 border-red-200', 'Fehler'], not_configured: ['bg-amber-50 text-amber-700 border-amber-200', 'Nicht konfiguriert'], degraded: ['bg-amber-50 text-amber-700 border-amber-200', 'Eingeschränkt'] };
    const [cls, label] = map[status] || ['bg-slate-50 text-slate-600 border-slate-200', status];
    return <Badge className={`${cls} border text-xs`}>{label}</Badge>;
  };

  const Spinner = () => <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{color:'var(--color-primary)'}} /></div>;

  return (
    <div className="space-y-6">
      <Card style={{backgroundColor:'var(--color-surface)',borderColor:'var(--color-border)'}}>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2" style={{fontFamily:'Plus Jakarta Sans,sans-serif',color:'var(--color-text-primary)'}}>
            <BookOpen className="w-5 h-5" /> Dokumentation & System
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={loadTab}>
            <TabsList className="bg-slate-100 border border-slate-200 p-1 mb-6">
              <TabsTrigger value="api" className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Server className="w-3.5 h-3.5 mr-1.5" />API-Referenz</TabsTrigger>
              <TabsTrigger value="setup" className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Settings className="w-3.5 h-3.5 mr-1.5" />Einrichtung</TabsTrigger>
              <TabsTrigger value="arch" className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Activity className="w-3.5 h-3.5 mr-1.5" />Architektur</TabsTrigger>
              <TabsTrigger value="security" className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Shield className="w-3.5 h-3.5 mr-1.5" />Sicherheit</TabsTrigger>
              <TabsTrigger value="tests" className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><TestTube className="w-3.5 h-3.5 mr-1.5" />Tests</TabsTrigger>
              <TabsTrigger value="health" className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Cpu className="w-3.5 h-3.5 mr-1.5" />Health Check</TabsTrigger>
            </TabsList>

            {/* API Reference */}
            <TabsContent value="api">
              {loading.api ? <Spinner /> : apiDocs && (
                <div className="space-y-6">
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <p className="text-sm font-medium text-slate-700">Authentifizierung: <code className="bg-slate-200 px-2 py-0.5 rounded text-xs">{apiDocs.authentication?.header}</code></p>
                    <p className="text-xs text-slate-500 mt-1">Token via: {apiDocs.authentication?.obtain} | Ablauf: {apiDocs.authentication?.expiry}</p>
                  </div>
                  {apiDocs.endpoints?.map((group, gi) => (
                    <div key={gi}>
                      <h3 className="text-base font-semibold mb-1" style={{color:'var(--color-text-primary)'}}>{group.group}</h3>
                      {group.description && <p className="text-xs text-slate-500 mb-3">{group.description}</p>}
                      <div className="border rounded-lg border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead><tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Methode</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Pfad</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Beschreibung</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Auth</th>
                          </tr></thead>
                          <tbody>
                            {group.endpoints?.map((ep, ei) => (
                              <tr key={ei}
                                className={`border-b border-slate-100 last:border-0 transition-colors ${ep.example ? 'hover:bg-blue-50/50 cursor-pointer' : 'hover:bg-slate-50/50'}`}
                                onClick={() => ep.example && setSelectedExample({ method: ep.method, path: ep.path, description: ep.description, example: ep.example })}
                              >
                                <td className="px-3 py-2"><Badge className={`text-xs border ${ep.method==='GET'?'bg-blue-50 text-blue-700 border-blue-200':ep.method==='POST'?'bg-green-50 text-green-700 border-green-200':ep.method==='PUT'?'bg-amber-50 text-amber-700 border-amber-200':'bg-red-50 text-red-700 border-red-200'}`}>{ep.method}</Badge></td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-700">{ep.path}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">
                                  {ep.description}
                                  {ep.example && <Code className="w-3 h-3 inline ml-1.5 text-blue-400" />}
                                </td>
                                <td className="px-3 py-2">{ep.auth ? <Badge className="bg-slate-100 text-slate-600 border-slate-200 border text-xs">JWT</Badge> : <Badge className="bg-slate-50 text-slate-400 border-slate-200 border text-xs">Offen</Badge>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Setup / Configuration */}
            <TabsContent value="setup">
              {loading.setup ? <Spinner /> : setupDocs && (
                <div className="space-y-6">
                  {setupDocs.sections?.map((section, si) => (
                    <Card key={si} style={{backgroundColor:'var(--color-surface)',borderColor:'var(--color-border)'}}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2" style={{color:'var(--color-text-primary)'}}>
                          <Settings className="w-4 h-4" /> {section.title}
                        </CardTitle>
                        <p className="text-xs" style={{color:'var(--color-text-secondary)'}}>{section.description}</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Steps */}
                        <div className="space-y-1.5">
                          {section.steps?.map((step, i) => (
                            <p key={i} className="text-sm text-slate-600">{step}</p>
                          ))}
                        </div>
                        {/* Parameters with copy */}
                        {section.parameters?.length > 0 && (
                          <div className="border rounded-lg border-slate-200 divide-y divide-slate-100 mt-3">
                            {section.parameters.map((p, pi) => (
                              <div key={pi} className="px-4 py-3 flex items-center justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium text-slate-500">{p.name}</p>
                                  {p.type === 'url' ? (
                                    <code className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded mt-1 inline-block break-all">{p.value}</code>
                                  ) : p.type === 'scopes' ? (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {p.value.split(', ').map((s, i) => <Badge key={i} className="bg-slate-100 text-slate-600 border-slate-200 border text-xs">{s}</Badge>)}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-slate-700 mt-0.5">{p.value}</p>
                                  )}
                                </div>
                                {p.type === 'url' && (
                                  <button className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex-shrink-0" title="Kopieren"
                                    onClick={() => { navigator.clipboard.writeText(p.value); toast.success('URL kopiert'); }}>
                                    <Copy className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Architecture */}
            <TabsContent value="arch">
              {loading.arch ? <Spinner /> : archDocs && (
                <div className="space-y-6">
                  <p className="text-sm" style={{color:'var(--color-text-secondary)'}}>{archDocs.overview}</p>
                  <div className="p-4 rounded-lg bg-slate-900 overflow-x-auto">
                    <pre className="text-green-400 text-xs font-mono whitespace-pre leading-relaxed">{archDocs.diagram}</pre>
                  </div>
                  <h3 className="text-base font-semibold" style={{color:'var(--color-text-primary)'}}>Komponenten</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {archDocs.components?.map((c, i) => (
                      <Card key={i} className="border-slate-200">
                        <CardContent className="pt-4">
                          <p className="font-medium text-sm text-slate-800">{c.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{c.tech}</p>
                          <p className="text-xs text-slate-600 mt-2">{c.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <h3 className="text-base font-semibold" style={{color:'var(--color-text-primary)'}}>Datenflüsse</h3>
                  <div className="space-y-2">
                    {archDocs.data_flow && Object.entries(archDocs.data_flow).map(([k, v]) => (
                      <div key={k} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs font-medium text-slate-500 uppercase">{k}</p>
                        <p className="text-sm text-slate-700 mt-1 font-mono">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Security */}
            <TabsContent value="security">
              {loading.security ? <Spinner /> : secDocs && (
                <div className="space-y-6">
                  {secDocs.sections?.map((section, si) => (
                    <div key={si}>
                      <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{color:'var(--color-text-primary)'}}>
                        <Shield className="w-4 h-4" /> {section.title}
                      </h3>
                      <ul className="space-y-2 mb-4">
                        {section.items?.map((item, ii) => (
                          <li key={ii} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      {section.data_categories && (
                        <div className="border rounded-lg border-slate-200 overflow-hidden mt-4">
                          <table className="w-full text-sm">
                            <thead><tr className="bg-slate-50 border-b border-slate-200">
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Kategorie</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Daten</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Speicherort</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Aufbewahrung</th>
                            </tr></thead>
                            <tbody>
                              {section.data_categories.map((dc, di) => (
                                <tr key={di} className="border-b border-slate-100 last:border-0">
                                  <td className="px-3 py-2 text-xs font-medium text-slate-700">{dc.category}</td>
                                  <td className="px-3 py-2 text-xs text-slate-600">{dc.data}</td>
                                  <td className="px-3 py-2 text-xs text-slate-600">{dc.storage}</td>
                                  <td className="px-3 py-2 text-xs text-slate-600">{dc.retention}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tests */}
            <TabsContent value="tests">
              {loading.tests ? <Spinner /> : testDocs && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      {label:'Test-Dateien', value:testDocs.summary?.test_files, icon:TestTube},
                      {label:'Test-Funktionen', value:testDocs.summary?.total_test_functions, icon:CheckCircle},
                      {label:'Test-Reports', value:testDocs.summary?.test_reports, icon:BookOpen},
                      {label:'Letztes Ergebnis', value:testDocs.summary?.latest_result?.backend || '–', icon:Activity},
                    ].map((s,i) => (
                      <Card key={i} className="border-slate-200"><CardContent className="pt-4 text-center">
                        <s.icon className="w-5 h-5 mx-auto mb-2 text-slate-400" />
                        <p className="text-2xl font-bold" style={{color:'var(--color-text-primary)'}}>{s.value}</p>
                        <p className="text-xs text-slate-500">{s.label}</p>
                      </CardContent></Card>
                    ))}
                  </div>
                  <h3 className="text-base font-semibold" style={{color:'var(--color-text-primary)'}}>Abdeckungsbereiche</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {testDocs.coverage_areas?.map((area, i) => (
                      <div key={i} className="p-3 rounded-lg border border-slate-200 flex items-start gap-3">
                        <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${area.covered?'text-green-500':'text-slate-300'}`} />
                        <div>
                          <p className="text-sm font-medium text-slate-700">{area.area}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{area.tests?.join(', ')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <h3 className="text-base font-semibold" style={{color:'var(--color-text-primary)'}}>Test-Dateien</h3>
                  <div className="border rounded-lg border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 border-b"><th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Datei</th><th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Tests</th><th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Zeilen</th></tr></thead>
                      <tbody>{testDocs.test_files?.map((f,i)=>(<tr key={i} className="border-b border-slate-100 last:border-0"><td className="px-3 py-2 text-xs font-mono text-slate-700">{f.file}</td><td className="px-3 py-2 text-xs text-right text-slate-600">{f.test_functions}</td><td className="px-3 py-2 text-xs text-right text-slate-500">{f.lines}</td></tr>))}</tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Health Check */}
            <TabsContent value="health">
              {loading.health ? <Spinner /> : healthData ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <StatusBadge status={healthData.overall_status} />
                      <span className="text-xs text-slate-500">{healthData.timestamp}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => loadTab('health')}><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Aktualisieren</Button>
                  </div>

                  {/* System Metrics */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <Card className="border-slate-200"><CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3"><Cpu className="w-4 h-4 text-blue-500" /><span className="text-sm font-medium text-slate-700">CPU</span></div>
                      <p className="text-2xl font-bold" style={{color:'var(--color-text-primary)'}}>{healthData.system?.cpu?.usage_percent}%</p>
                      <p className="text-xs text-slate-500">{healthData.system?.cpu?.cores} Kerne | Load: {healthData.system?.cpu?.load_avg_1m}</p>
                    </CardContent></Card>
                    <Card className="border-slate-200"><CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-green-500" /><span className="text-sm font-medium text-slate-700">Speicher (RAM)</span></div>
                      <p className="text-2xl font-bold" style={{color:'var(--color-text-primary)'}}>{healthData.system?.memory?.usage_percent}%</p>
                      <p className="text-xs text-slate-500">{healthData.system?.memory?.used_gb} / {healthData.system?.memory?.total_gb} GB</p>
                      <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width:`${healthData.system?.memory?.usage_percent}%`, backgroundColor: healthData.system?.memory?.usage_percent > 80 ? '#ef4444' : 'var(--color-primary)'}} /></div>
                    </CardContent></Card>
                    <Card className="border-slate-200"><CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3"><HardDrive className="w-4 h-4 text-amber-500" /><span className="text-sm font-medium text-slate-700">Festplatte</span></div>
                      <p className="text-2xl font-bold" style={{color:'var(--color-text-primary)'}}>{healthData.system?.disk?.usage_percent}%</p>
                      <p className="text-xs text-slate-500">{healthData.system?.disk?.used_gb} / {healthData.system?.disk?.total_gb} GB</p>
                      <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width:`${healthData.system?.disk?.usage_percent}%`, backgroundColor: healthData.system?.disk?.usage_percent > 80 ? '#ef4444' : 'var(--color-primary)'}} /></div>
                    </CardContent></Card>
                  </div>

                  {/* Database */}
                  <h3 className="text-base font-semibold flex items-center gap-2" style={{color:'var(--color-text-primary)'}}><Database className="w-4 h-4" /> Datenbank</h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    {[
                      {label:'Status', value: <StatusBadge status={healthData.database?.status} />},
                      {label:'Latenz', value:`${healthData.database?.latency_ms} ms`},
                      {label:'Collections', value:healthData.database?.collections},
                      {label:'Dokumente', value:healthData.database?.documents?.toLocaleString()},
                    ].map((m,i)=>(<Card key={i} className="border-slate-200"><CardContent className="pt-4 text-center"><p className="text-xs text-slate-500 mb-1">{m.label}</p><p className="text-lg font-semibold" style={{color:'var(--color-text-primary)'}}>{m.value}</p></CardContent></Card>))}
                  </div>

                  {/* API Tests */}
                  <h3 className="text-base font-semibold" style={{color:'var(--color-text-primary)'}}>API-Selbsttest</h3>
                  <div className="border rounded-lg border-slate-200 divide-y divide-slate-100">
                    {healthData.api_tests?.map((t,i)=>(
                      <div key={i} className="px-4 py-3 flex items-center justify-between">
                        <span className="text-sm text-slate-700">{t.name}</span>
                        <StatusBadge status={t.status} />
                      </div>
                    ))}
                  </div>

                  {/* Collections */}
                  <h3 className="text-base font-semibold" style={{color:'var(--color-text-primary)'}}>Collections</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {healthData.collections?.map((c,i)=>(
                      <div key={i} className="p-3 rounded-lg border border-slate-200 text-center">
                        <p className="text-lg font-bold" style={{color:'var(--color-text-primary)'}}>{c.documents}</p>
                        <p className="text-xs text-slate-500 truncate" title={c.name}>{c.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p className="text-sm text-slate-500 text-center py-8">Health Check wird geladen...</p>}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Example Dialog */}
      <Dialog open={!!selectedExample} onOpenChange={() => setSelectedExample(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{fontFamily:'Plus Jakarta Sans,sans-serif'}}>
              <Code className="w-4 h-4" />
              <Badge className={`text-xs border ${selectedExample?.method==='GET'?'bg-blue-50 text-blue-700 border-blue-200':'bg-green-50 text-green-700 border-green-200'}`}>{selectedExample?.method}</Badge>
              <span className="font-mono text-sm">{selectedExample?.path}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 -mt-2">{selectedExample?.description}</p>
          <div className="relative mt-2">
            <pre className="p-4 rounded-lg bg-slate-900 text-green-400 text-xs font-mono whitespace-pre-wrap overflow-x-auto leading-relaxed">{selectedExample?.example}</pre>
            <button
              className="absolute bottom-3 right-3 p-2 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
              data-testid="copy-example-btn"
              title="In Zwischenablage kopieren"
              onClick={() => {
                navigator.clipboard.writeText(selectedExample?.example || '');
                toast.success('Beispiel in Zwischenablage kopiert');
              }}
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
